import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Loader2, ChevronRight, Star } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function MyBadgesWidget() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState([]);
  const [totalBadges, setTotalBadges] = useState(0);

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const res = await apiRequest('/recognition/my-badges');
      setBadges(res.badges || []);
      setTotalBadges(res.total_badges || 0);
    } catch (err) {
      console.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-48">
          <Loader2 className="animate-spin text-slate-400" size={24} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full" data-testid="my-badges-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Award className="text-purple-500" size={18} />
            My Badges
          </span>
          <Button variant="ghost" size="sm" onClick={() => navigate('/gamification')}>
            <ChevronRight size={16} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {badges.length > 0 ? (
          <>
            <div className="flex items-center gap-2 mb-3">
              <Star className="text-yellow-500" size={16} />
              <span className="text-sm text-slate-600">
                {totalBadges} badge{totalBadges !== 1 ? 's' : ''} earned
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {badges.slice(0, 4).map((badge) => (
                <div
                  key={badge.badge_type}
                  className="p-2 rounded-lg border border-slate-200 text-center"
                  style={{ backgroundColor: (badge.badge_info?.color || '#6366f1') + '10' }}
                >
                  <span className="text-2xl">{badge.badge_info?.emoji || '🏅'}</span>
                  <p className="text-xs font-medium mt-1 truncate">{badge.badge_info?.name || badge.badge_type}</p>
                  {badge.count > 1 && (
                    <Badge variant="outline" className="mt-1 text-xs">x{badge.count}</Badge>
                  )}
                </div>
              ))}
            </div>
            {badges.length > 4 && (
              <p className="text-xs text-slate-500 text-center mt-2">
                +{badges.length - 4} more badges
              </p>
            )}
          </>
        ) : (
          <div className="text-center py-6 text-slate-500 text-sm">
            <Award size={32} className="mx-auto mb-2 opacity-30" />
            <p>No badges earned yet</p>
            <p className="text-xs mt-1">Keep up the great work!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
